import { Info, SquareArrowDownRight, SquareArrowUpRight } from "lucide-react";

import { type ClaimTypeName, isCancellationLikeClaimType } from "../lib/claim-types";
import { formatInTimezone, minTwoDigits, timezoneToParsedOffset } from "../lib/dates";

interface Airport {
  name: string;
  iata: string;
  timezone: string;
  city: string;
}

interface FlightOverviewItineraryProps {
  claimTypeName: ClaimTypeName | null;
  scheduledDepartureUTC: Date;
  scheduledArrivalUTC: Date;
  departureAirport: Airport;
  arrivalAirport: Airport;
  utc: boolean;
  detailAvailable?: boolean;
  onShowDetail?: () => void;
}

function AirportBlock({ airport, referenceDate }: { airport: Airport; referenceDate: Date }) {
  const offset = timezoneToParsedOffset(airport.timezone, referenceDate);
  return (
    <div className="my-3 ml-4 flex flex-col items-start">
      <p className="mb-1 text-left text-xl font-bold">
        {airport.city}{" "}
        <span className="text-base font-normal">
          (<span className="font-bold">{airport.iata}</span>)
        </span>
      </p>
      <p className="text-left text-sm text-gray-700">{airport.name}</p>
      <p className="mt-2 text-xs">
        {airport.timezone} (GMT {offset.sign}
        {minTwoDigits(offset.hours)}:{minTwoDigits(offset.minutes)})
      </p>
    </div>
  );
}

/** Port of FlightOverviewItinerary.vue. */
export function FlightOverviewItinerary(props: FlightOverviewItineraryProps) {
  const departureDate = formatInTimezone(
    props.scheduledDepartureUTC,
    props.utc ? "UTC" : props.departureAirport.timezone,
    "d MMMM yyyy",
  );

  const accentClass = isCancellationLikeClaimType(props.claimTypeName)
    ? "border-l-2 border-red-600"
    : props.claimTypeName
      ? "border-l-2 border-amber-500"
      : "border-l-2 border-blue-500";

  return (
    <div>
      <div className="flex h-10 flex-row items-center border-b bg-gray-100">
        <div className="ml-4 inline-flex flex-2 items-center text-base font-bold text-gray-500 uppercase">
          <SquareArrowUpRight className="mr-2 h-5 w-5 shrink-0 text-zinc-500" />
          Départ
          <span className="ml-4 text-xs leading-[1.15rem] font-medium text-gray-900">{departureDate}</span>
        </div>
        <div className="ml-4 inline-flex flex-2 items-center text-base font-bold text-gray-500 uppercase">
          <SquareArrowDownRight className="mr-2 h-5 w-5 shrink-0 text-zinc-500" />
          Arrivée
          {props.detailAvailable ? (
            <button
              type="button"
              className="ml-4 inline-flex items-center text-xs font-medium text-sky-600 normal-case hover:text-sky-700"
              onClick={props.onShowDetail}
            >
              <Info className="mr-1 h-3.5 w-3.5" />
              Voir le détail
            </button>
          ) : null}
        </div>
      </div>
      <div className={`flex flex-row ${accentClass}`}>
        <div className="flex-2">
          <AirportBlock airport={props.departureAirport} referenceDate={props.scheduledDepartureUTC} />
        </div>
        <div className="flex-2">
          <AirportBlock airport={props.arrivalAirport} referenceDate={props.scheduledArrivalUTC} />
        </div>
      </div>
    </div>
  );
}
